import type { ReactNode } from 'react';
import { useOrgOpcional } from '@/features/team/OrgProvider';
import { APP_NAME } from '@/app/constants';

/** Las pantallas a las que se llega desde el tablero. */
export type DestinoInicio =
  | 'nuevo-trabajo'
  | 'trabajos'
  | 'facturar'
  | 'resumen'
  | 'clientes'
  | 'honorarios'
  | 'precios'
  | 'liquidacion'
  | 'config';

/**
 * Inicio.
 *
 * **Es un tablero de accesos, no un panel de control.** Llegó a mostrar los
 * indicadores del mes y cuánto le correspondía a cada participante, y en el
 * teléfono eso resultó peor de lo que sonaba: obligaba a leer números antes de
 * poder elegir a dónde ir, tardaba en aparecer, y repetía —peor y más chico—
 * lo que Liquidación ya muestra completo. Una pantalla que se abre veinte
 * veces por día tiene que contestar una sola pregunta, *¿a dónde voy?*, y
 * contestarla antes de terminar de dibujarse.
 *
 * Por eso acá no se pide ningún dato. La información económica no se escondió:
 * está en su sección, entera y con lugar para explicarse.
 *
 * El orden es el del día de trabajo: primero cargar lo que se hizo, después
 * facturarlo y cobrarlo, después repartirlo. Lo de consulta —clientes,
 * tarifario, precios— va después, y la configuración fuera de la grilla.
 */
export function HomePage({ onIr }: { onIr: (destino: DestinoInicio | 'mas') => void }) {
  // El nombre de la veterinaria, para saber en cuál se está cuando hay más de
  // una. No es una consulta: ya está en memoria. Se pide sin exigir: es un
  // rótulo, y una pantalla que se puede dibujar sin él no tiene por qué
  // reventar cuando falta.
  const organizacion = useOrgOpcional()?.activeOrg?.name ?? null;

  return (
    <div className="flex flex-col gap-6 p-4" data-testid="home">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{APP_NAME}</h1>
        {organizacion && <p className="mt-0.5 text-sm text-[var(--tinta-suave)]">{organizacion}</p>}
      </div>

      {/* Dos columnas y alturas iguales. A 360 px cada tarjeta queda en unos
          160 px de ancho, que es cómodo para el pulgar y alcanza para el
          rótulo sin cortarlo. */}
      <div className="grid grid-cols-2 gap-3">
        <Acceso
          icono={<IconoMas />}
          rotulo="Nuevo trabajo"
          nota="Registrar trabajo"
          destacado
          onClick={() => onIr('nuevo-trabajo')}
        />
        <Acceso
          icono={<IconoLista />}
          rotulo="Trabajos"
          nota="Cargar y corregir"
          onClick={() => onIr('trabajos')}
        />
        {/* Facturar.
            Era lo único del día de trabajo que no tenía pantalla propia. Se
            hacía a mano entre dos: el resumen va por fecha —así se carga— y
            facturar va por cliente, así que un cliente con cuatro trabajos
            había que irlo juntando a ojo entre los demás y sumar las cantidades
            en un papel.
            Va pegado a Trabajos y antes del Resumen, siguiendo el orden del
            día: cargar, facturar, ver cómo va, repartir. */}
        <Acceso
          icono={<IconoFactura />}
          rotulo="Facturar"
          nota="Qué le cobro a cada cliente"
          onClick={() => onIr('facturar')}
        />
        {/* Resumen del mes.
            Faltaba, y era un agujero de verdad: el destino «resumen» existía en
            el código desde siempre pero NINGUNA pantalla lo enlazaba, ni acá ni
            en «Configuración y más». La pantalla estaba escrita, andaba, y no
            se podía llegar a ella.
            Va antes de Liquidación a propósito, siguiendo el orden del día de
            trabajo: cargar, facturar, VER CÓMO VA, y recién al final del mes
            repartir. Se mira todos los días; liquidar, una vez por mes. */}
        <Acceso
          icono={<IconoResumen />}
          rotulo="Resumen del mes"
          nota="Cómo viene el mes"
          onClick={() => onIr('resumen')}
        />
        <Acceso
          icono={<IconoReparto />}
          rotulo="Liquidación"
          nota="Cuánto le toca a cada uno"
          onClick={() => onIr('liquidacion')}
        />
        <Acceso
          icono={<IconoGente />}
          rotulo="Clientes"
          nota="Clientes y campos"
          onClick={() => onIr('clientes')}
        />
        <Acceso
          icono={<IconoTarifa />}
          rotulo="Honorarios"
          nota="Tarifario"
          onClick={() => onIr('honorarios')}
        />
        <Acceso
          icono={<IconoPrecio />}
          rotulo="Precios"
          nota="Novillo y combustible"
          onClick={() => onIr('precios')}
        />
      </div>

      {/* Fuera de la grilla y con menor jerarquía a propósito: se entra pocas
          veces y no tiene que competir con lo de todos los días. */}
      <button
        type="button"
        onClick={() => onIr('mas')}
        className="inline-flex min-h-11 items-center gap-2 self-start px-1 text-sm text-[var(--tinta-suave)]"
        data-testid="home-config"
      >
        <IconoEngranaje />
        Configuración y más
      </button>
    </div>
  );
}

function Acceso({
  icono,
  rotulo,
  nota,
  destacado = false,
  onClick,
}: {
  icono: ReactNode;
  rotulo: string;
  nota: string;
  /** La acción que más se hace. Se marca con el color de la aplicación. */
  destacado?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex min-h-28 flex-col justify-between rounded-[var(--radius-superficie)] p-4 text-left transition-colors ${
        destacado
          ? 'bg-[var(--accion)] text-[var(--sobre-accion)]'
          : 'bg-[var(--superficie)] text-[var(--tinta)] hover:bg-[var(--superficie-suave)]'
      }`}
      data-testid={`home-acceso-${rotulo.toLowerCase().replace(/[^a-z]+/g, '-')}`}
    >
      <span className={destacado ? '' : 'text-[var(--tinta-suave)]'} aria-hidden>
        {icono}
      </span>
      <span className="mt-3 block">
        {/* El nombre siempre visible: un icono solo obliga a adivinar, y en el
            campo con guantes y sol de frente adivinar sale caro. */}
        <span className="block font-semibold leading-tight">{rotulo}</span>
        <span
          className={`mt-0.5 block text-xs ${
            destacado ? 'opacity-80' : 'text-[var(--tinta-suave)]'
          }`}
        >
          {nota}
        </span>
      </span>
    </button>
  );
}

/* ---------------------------------------------------------------------------
   Iconos
   ---------------------------------------------------------------------------
   Una familia sola: trazo, mismo grosor, mismo tamaño, sin relleno. Antes
   convivían un `＋` de ancho completo, emojis en los estados de los trabajos y
   flechas tipográficas; tres estilos distintos en la misma aplicación se leen
   como tres aplicaciones.

   Van acá, dibujados a mano, y no como dependencia: son seis, pesan nada, y
   una librería de iconos entera para seis figuras es traer mil para usar seis.
   ------------------------------------------------------------------------- */

function Svg({ children }: { children: ReactNode }) {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {children}
    </svg>
  );
}

function IconoMas() {
  return (
    <Svg>
      <path d="M12 5v14M5 12h14" />
    </Svg>
  );
}

function IconoLista() {
  return (
    <Svg>
      <path d="M8 6h12M8 12h12M8 18h12M3.5 6h.01M3.5 12h.01M3.5 18h.01" />
    </Svg>
  );
}

/** Facturar: una hoja con renglones. */
function IconoFactura() {
  return (
    <Svg>
      <path d="M5.5 3.5h13v17l-2.2-1.4-2.2 1.4-2.1-1.4-2.2 1.4-2.1-1.4-2.2 1.4v-17Z" />
      <path d="M9 8.5h6M9 12.5h6" />
    </Svg>
  );
}

/** Reparto: algo que se divide en dos. */
/** Dos columnas y una raya: lo que gana cada uno, comparado. */
function IconoResumen() {
  return (
    <Svg>
      <path d="M4 20h16" />
      <rect x="6.5" y="11" width="4" height="9" rx="0.8" />
      <rect x="13.5" y="5" width="4" height="15" rx="0.8" />
    </Svg>
  );
}

function IconoReparto() {
  return (
    <Svg>
      <path d="M12 4v6M12 10l-5 4M12 10l5 4" />
      <circle cx="12" cy="4" r="1.6" />
      <circle cx="6" cy="16" r="2.4" />
      <circle cx="18" cy="16" r="2.4" />
    </Svg>
  );
}

function IconoGente() {
  return (
    <Svg>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3.5 19a5.5 5.5 0 0 1 11 0" />
      <path d="M16 5.5a3.2 3.2 0 0 1 0 6M17.5 19a5.5 5.5 0 0 0-2-4.2" />
    </Svg>
  );
}

/** Tarifario: una etiqueta de precio. */
function IconoTarifa() {
  return (
    <Svg>
      <path d="M3.5 10.5V4.5a1 1 0 0 1 1-1h6l9 9-7 7-9-9Z" />
      <circle cx="7.5" cy="7.5" r="1.2" />
    </Svg>
  );
}

/** Precio de referencia: una medida que sube y baja. */
function IconoPrecio() {
  return (
    <Svg>
      <path d="M4 18V8M10 18V4M16 18v-7M22 18H2" />
    </Svg>
  );
}

function IconoEngranaje() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1" />
    </svg>
  );
}
